import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  Stack,
  Button,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Pagination,
} from "@mui/material";
import { Edit, Delete, Print, Search } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useDelete from "../../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const AmbulancePatientPaymentList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDialog, setOpenDialog] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);

  const {
    data: payments,
    refetch,
    isLoading,
    isError,
  } = useFetchObjects(
    ["ambulance-patient-payments", query, dateFilter, page, rowsPerPage],
    `ambulance-patient-payments/?page=${page}&page_size=${rowsPerPage}&search=${query}&date=${dateFilter}`,
    token
  );

  const { handleDelete, DeleteMessageComponent, ConfirmDialog } = useDelete("ambulance-patient-payments", token);

  useEffect(() => {
    refetch();
  }, [query, dateFilter, refetch, page, rowsPerPage]);

  useEffect(() => {
    if (payments && payments.count !== undefined) {
      setTotalPayments(payments.count);
    }
  }, [payments]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleEditClick = (payment) => {
    setCurrentPayment(payment);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPayment(null);
  };

  const handlePrintClick = (payment) => {
    setPrintData(payment);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
          <html>
            <head>
              <title>Ambulance Patient Payment Receipt</title>
              <style>
                * { box-sizing: border-box; }
                body {
                  margin: 0;
                  padding: 24px;
                  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
                  background: #eef2f7;
                  color: #14213d;
                }
                .receipt-shell {
                  max-width: 860px;
                  margin: 0 auto;
                }
                .receipt-card {
                  background: #fff;
                  border: 1px solid #d7e0ea;
                  border-radius: 18px;
                  overflow: hidden;
                  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
                }
                .receipt-header {
                  padding: 24px 28px 18px;
                  background: linear-gradient(135deg, #0f4c81 0%, #1f6aa5 100%);
                  color: #fff;
                }
                .receipt-kicker {
                  margin: 0 0 8px;
                  font-size: 12px;
                  letter-spacing: 0.12em;
                  text-transform: uppercase;
                  opacity: 0.85;
                }
                .receipt-title-row {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  gap: 16px;
                }
                .receipt-title {
                  margin: 0;
                  font-size: 30px;
                  font-weight: 700;
                  line-height: 1.2;
                }
                .receipt-date {
                  text-align: right;
                  font-size: 14px;
                  line-height: 1.6;
                  white-space: nowrap;
                }
                .receipt-body {
                  padding: 28px;
                }
                .amount-summary {
                  display: grid;
                  grid-template-columns: 1.2fr 0.8fr;
                  gap: 16px;
                  margin-bottom: 24px;
                }
                .amount-box,
                .charge-box {
                  padding: 18px 20px;
                  border-radius: 14px;
                }
                .amount-box {
                  background: #f8fbff;
                  border: 1px solid #d8e7f5;
                }
                .charge-box {
                  background: #f7fbf7;
                  border: 1px solid #dbeadf;
                }
                .amount-label {
                  margin: 0;
                  color: #5b6b7f;
                  font-size: 13px;
                  text-transform: uppercase;
                  letter-spacing: 0.08em;
                }
                .amount-value {
                  margin: 6px 0 0;
                  font-size: 30px;
                  font-weight: 800;
                  color: #0f4c81;
                }
                .charge-row {
                  display: flex;
                  justify-content: space-between;
                  gap: 12px;
                  margin-top: 10px;
                  font-size: 15px;
                }
                .charge-row strong {
                  color: #1d3557;
                }
                .details-grid {
                  display: grid;
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                  gap: 14px;
                }
                .detail-item {
                  padding: 14px 16px;
                  border-radius: 12px;
                  background: #fcfdff;
                  border: 1px solid #e5ebf2;
                }
                .detail-label {
                  margin: 0 0 6px;
                  font-size: 12px;
                  color: #6b7a8c;
                  text-transform: uppercase;
                  letter-spacing: 0.08em;
                }
                .detail-value {
                  margin: 0;
                  font-size: 17px;
                  font-weight: 600;
                  color: #14213d;
                  word-break: break-word;
                }
                .receipt-footer {
                  margin-top: 24px;
                  padding-top: 18px;
                  border-top: 1px dashed #ccd6e0;
                  font-size: 13px;
                  color: #6b7a8c;
                  text-align: center;
                }
                @media print {
                  body {
                    padding: 0;
                    background: #fff;
                  }
                  .receipt-card {
                    border: none;
                    border-radius: 0;
                    box-shadow: none;
                  }
                }
                @media (max-width: 640px) {
                  body {
                    padding: 12px;
                  }
                  .receipt-header,
                  .receipt-body {
                    padding: 18px;
                  }
                  .receipt-title-row {
                    display: block;
                  }
                  .receipt-date {
                    text-align: left;
                    margin-top: 10px;
                  }
                  .amount-summary,
                  .details-grid {
                    grid-template-columns: 1fr;
                  }
                }
              </style>
            </head>
            <body>${printRef.current.innerHTML}</body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  return (
    <Grid container spacing={4} mb={4} pb={5}>
      <Grid item xs={12}>
        <Box
          padding="25px"
          backgroundColor="white"
          boxShadow={3}
          borderRadius={2}
        >
          <Box display="flex" justifyContent="space-between" mb={4}>
            <Typography variant="h5" fontWeight={700} color="primary">
              {t("finance.ambulancePatientPaymentList", { defaultValue: "Ambulance Patient Payments List" })}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/finance/fees-collection/ambulance/patient/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("common.addNew", { defaultValue: "Add New" })}
            </Button>
          </Box>

          {/* Filters */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            mb={3}
            justifyContent="space-between"
          >
            <TextField
              label={t("reception.searchByPatient")}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: <Search color="action" />,
              }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <TextField
              label={t("reception.filterByDate")}
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </Stack>

          {/* Table */}
          {isLoading ? (
            <Box textAlign="center" mt={4}>
              <CircularProgress />
              <Typography variant="body2">{t("reception.loading")}</Typography>
            </Box>
          ) : isError ? (
            <Box textAlign="center" color="red" mt={4}>
              <Typography variant="body2">{t("reception.errorFetchingData")}</Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.date", { defaultValue: "Date" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.patientName", { defaultValue: "Patient Name" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.patientPhone", { defaultValue: "Phone" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.ambulanceCode", { defaultValue: "Ambulance Code" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.driver", { defaultValue: "Driver" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.tripCharge", { defaultValue: "Trip Charge" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.discount", { defaultValue: "Discount" })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.totalAmount", { defaultValue: "Total Amount" })}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("finance.actions", { defaultValue: "Actions" })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments?.results?.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell align="center">
                        <Typography>{new Date(payment.payment_date).toLocaleDateString()}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment.patient?.firstname} {payment.patient?.lastname}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{payment.patient_phone}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{payment.ambulance_code}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment.driver ? `${payment.driver.firstname} ${payment.driver.lastname}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{payment.trip_charge}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{payment.discount}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{payment.final_amount}</Typography>
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          size="small"
                          aria-label="edit"
                          onClick={() => handleEditClick(payment)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="print"
                          onClick={() => handlePrintClick(payment)}
                        >
                          <Print />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="delete"
                          onClick={() => handleDelete(payment.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Total Count */}
        <Box mt={2} textAlign="right">
          <Typography variant="body2">
            {t("reception.totalRecords", { defaultValue: "Total Records" })}:{" "}
            <Typography component="span" fontWeight="bold">
              {totalPayments}
            </Typography>
          </Typography>
        </Box>
      </Grid>
      <DeleteMessageComponent />
      <ConfirmDialog />
      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <div className="receipt-shell">
            <div className="receipt-card">
              <div className="receipt-header">
                <p className="receipt-kicker">Finance Department</p>
                <div className="receipt-title-row">
                  <h2 className="receipt-title">Ambulance Patient Payment Receipt</h2>
                  <div className="receipt-date">
                    <div><strong>Date:</strong> {new Date(printData.payment_date).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>

              <div className="receipt-body">
                <div className="amount-summary">
                  <div className="amount-box">
                    <p className="amount-label">Final Amount</p>
                    <p className="amount-value">{printData.final_amount}</p>
                  </div>
                  <div className="charge-box">
                    <p className="amount-label">Trip Summary</p>
                    <div className="charge-row">
                      <span>Trip Charge</span>
                      <strong>{printData.trip_charge}</strong>
                    </div>
                    <div className="charge-row">
                      <span>Discount</span>
                      <strong>{printData.discount}</strong>
                    </div>
                  </div>
                </div>

                <div className="details-grid">
                  <div className="detail-item">
                    <p className="detail-label">Patient</p>
                    <p className="detail-value">{`${printData.patient?.firstname || ""} ${printData.patient?.lastname || ""}`.trim() || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Patient Phone</p>
                    <p className="detail-value">{printData.patient_phone || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Ambulance Code</p>
                    <p className="detail-value">{printData.ambulance_code || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Plate Number</p>
                    <p className="detail-value">{printData.plate_number || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Driver</p>
                    <p className="detail-value">{printData.driver ? `${printData.driver.firstname || ""} ${printData.driver.lastname || ""}`.trim() : "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Pickup Location</p>
                    <p className="detail-value">{printData.pickup_location || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Destination</p>
                    <p className="detail-value">{printData.destination || "N/A"}</p>
                  </div>
                  <div className="detail-item">
                    <p className="detail-label">Receipt Type</p>
                    <p className="detail-value">Patient Ambulance Payment</p>
                  </div>
                </div>

                <div className="receipt-footer">
                  This receipt confirms the ambulance patient payment record.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Grid>
  );
};

export default AmbulancePatientPaymentList;
